import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface Promotion {
  id: number;
  source: string;
  discount_code: string | null;
  title: string;
  description: string | null;
  image_url: string | null;
  image_filename: string | null;
  package_code: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface PromotionsResponse {
  success: boolean;
  data: Promotion[];
}

interface PromotionResponse {
  success: boolean;
  data: Promotion;
  message?: string;
}

export function usePromotions() {
  return useQuery({
    queryKey: ['promotions'],
    queryFn: async () => (await apiClient<PromotionsResponse>('/promotions')).data,
  });
}

function useInvalidatePromotions() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['promotions'] });
}

export interface PromotionDraft {
  id?: number;
  source?: string;
  discount_code?: string | null;
  title?: string;
  description?: string | null;
  image_url?: string | null;
  image_filename?: string | null;
  package_code?: string | null;
}

export function useCreatePromotion() {
  const invalidate = useInvalidatePromotions();
  return useMutation({
    mutationFn: async (draft: PromotionDraft) => (await apiClient<PromotionResponse>('/promotions', { method: 'POST', body: JSON.stringify(draft) })).data,
    onSuccess: invalidate,
  });
}

export function useUpdatePromotion() {
  const invalidate = useInvalidatePromotions();
  return useMutation({
    mutationFn: async ({ id, ...fields }: PromotionDraft & { id: number }) =>
      (await apiClient<PromotionResponse>(`/promotions/${id}`, { method: 'PUT', body: JSON.stringify(fields) })).data,
    onSuccess: invalidate,
  });
}

export function useDeletePromotion() {
  const invalidate = useInvalidatePromotions();
  return useMutation({
    mutationFn: (id: number) => apiClient<{ success: boolean; message: string }>(`/promotions/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useActivatePromotion() {
  const invalidate = useInvalidatePromotions();
  return useMutation({
    mutationFn: async (id: number) => (await apiClient<PromotionResponse>(`/promotions/${id}/activate`, { method: 'POST' })).data,
    onSuccess: invalidate,
  });
}

export function useDeactivatePromotion() {
  const invalidate = useInvalidatePromotions();
  return useMutation({
    mutationFn: async (id: number) => (await apiClient<PromotionResponse>(`/promotions/${id}/deactivate`, { method: 'POST' })).data,
    onSuccess: invalidate,
  });
}
